import { Fragment } from "react";
import { Link } from "@inertiajs/react";

import AppSidebar from "@/components/AppSidebar";
import Flash from "@/components/Flash";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  Breadcrumb as BreadcrumbNav,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { TBreadcrumbItem } from "@/types/breadcrumb";

const EMPTY_BREADCRUMBS: TBreadcrumbItem[] = [];

function HeaderBreadcrumbs({ breadcrumbs }: { breadcrumbs: TBreadcrumbItem[] }) {
  if (breadcrumbs.length === 0) return null;

  return (
    <BreadcrumbNav>
      <BreadcrumbList>
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <Fragment key={item.label}>
              <BreadcrumbItem>
                {isLast || !item.href ? (
                  <BreadcrumbPage className={item.className}>{item.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink render={<Link href={item.href} />}>{item.label}</BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </BreadcrumbNav>
  );
}

export default function Main({
  children,
  breadcrumbs = EMPTY_BREADCRUMBS,
}: {
  children: React.ReactNode;
  breadcrumbs?: TBreadcrumbItem[];
}) {
  return (
    <TooltipProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-10 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="my-auto mr-2 h-4" />
            <HeaderBreadcrumbs breadcrumbs={breadcrumbs} />
          </header>
          <Flash />
          <div className="flex flex-1 flex-col gap-4 p-4">{children}</div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}
